/**
 * Caps for a `.claude` folder packed as a zip (spec §6.2 Ingest table: "5 MB compressed,
 * 20 MB uncompressed, at most 500 files, at most 256 KB per file. Reject symlinks, absolute paths,
 * `..`, device files, nested archives."), for the node CLI's local pack step (T3, `main.ts` →
 * `agent/import-materialize.ts`).
 *
 * The vocabulary below is the hub zip ingest's, not this file's invention: AC23 asks for an
 * oversized or symlinked folder to be "rejected locally with the same error codes as the zip
 * ingest", which makes the ingest the reference and the local pack step the side that matches.
 * These names are therefore a subset of `ImportIngestErrorCode` in `apps/server/src/agent/
 * import-ingest.ts` — the ingest also has codes no local pack step can raise (`ZIP_INVALID`,
 * `ZIP_ENCRYPTED`, the `GIT_*` set). Since task #84 that is structural rather than a convention
 * two sides have to remember: the ingest defines `ImportIngestErrorCode` as `ZipRejectionCode |
 * <its own extras>` and derives its four numeric caps from `ZIP_CAPS`, so renaming a code or
 * moving a limit here changes both sides at once and they cannot drift apart. Before #84 the
 * ingest kept a hand-written copy of both, and the assignment in
 * `apps/server/test/agent-units.test.ts` was what caught a divergence; that assignment is now
 * tautological and kept only as a regression guard for anyone replacing the derived union with a
 * literal one again. The loop just below it still bites, though: it hardcodes these spellings, so
 * renaming a code here without updating it fails that test. The behavioural check that the two
 * code paths really do agree lives in `apps/server/test/agent-import-ingest.test.ts`, which
 * compares what `ingestZip` and `packFolderToZip` actually throw for the same rejection.
 */

export const ZIP_CAPS = {
  maxCompressedBytes: 5 * 1024 * 1024,
  maxUncompressedBytes: 20 * 1024 * 1024,
  maxFiles: 500,
  maxFileBytes: 256 * 1024,
} as const;

/** Largest body a node will buffer from `GET /api/agent-imports/:id/bundle` (`HubClient.importBundle`).
 *
 * This is NOT `maxCompressedBytes` alone. A git-sourced import is stored as `import-plan.ts`'s
 * `serializeFiles` encoding of its checkout, uncompressed, and `ingestGit` admits a checkout up to
 * `maxUncompressedBytes` (20 MB) across at most `maxFiles` entries. Capping the response at 5 MB
 * would reject a git import the hub already accepted. So the cap is the uncompressed total plus the
 * serializer's framing: an 8-byte `[pathLen][dataLen]` header per entry and a path of at most
 * `BUNDLE_MAX_PATH_BYTES` (4096, the POSIX PATH_MAX an on-disk checkout cannot exceed). A zip-sourced
 * bundle is at most `maxCompressedBytes` (ingest-checked) and always fits under this too. */
export const BUNDLE_SERIALIZED_ENTRY_HEADER_BYTES = 8;
export const BUNDLE_MAX_PATH_BYTES = 4096;
export const BUNDLE_MAX_BYTES = ZIP_CAPS.maxUncompressedBytes + ZIP_CAPS.maxFiles * (BUNDLE_SERIALIZED_ENTRY_HEADER_BYTES + BUNDLE_MAX_PATH_BYTES);

export const ZIP_REJECTION_CODES = [
  "TOO_MANY_FILES",
  "UNCOMPRESSED_TOO_LARGE",
  "ZIP_COMPRESSED_TOO_LARGE",
  "FILE_TOO_LARGE",
  "SYMLINK",
  "ABSOLUTE_PATH",
  "PATH_TRAVERSAL",
  "DEVICE_FILE",
  "NESTED_ARCHIVE",
] as const;
export type ZipRejectionCode = (typeof ZIP_REJECTION_CODES)[number];

export class ZipCapError extends Error {
  readonly code: ZipRejectionCode;
  constructor(code: ZipRejectionCode, message: string) {
    super(message);
    this.code = code;
  }
}
